// src/features/calendar/components/CalendarSidebar.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useCalendarStore } from '../store/calendarStore';
import { useCalendarApi } from '../hooks/useCalendarApi';
import { EVENT_TYPES } from '../constants/eventTypes';
import type { CalendarEvent } from '../types/calendar.types';
import { 
  CalendarDaysIcon, 
  EyeIcon, 
  EyeSlashIcon,
  PlusIcon,
  ShareIcon,
  Cog6ToothIcon,
  XMarkIcon,
  CheckIcon,
  TrashIcon,
  PencilIcon,
  ArrowPathIcon,
  FunnelIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  Bars3Icon,
  CalendarIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';

// Priority levels
const PRIORITY_LEVELS = [
  { id: 'urgent', label: 'Urgent', color: 'red' },
  { id: 'high', label: 'High', color: 'orange' },
  { id: 'medium', label: 'Medium', color: 'yellow' },
  { id: 'low', label: 'Low', color: 'green' },
];

interface CalendarSidebarProps {
  onCreateEvent?: () => void;
}

const CalendarSidebar: React.FC<CalendarSidebarProps> = ({ onCreateEvent }) => {
  const {
    calendars,
    visibleCalendarIds,
    events,
    isCalendarSidebarOpen,
    setCalendarSidebarOpen,
    toggleCalendarVisibility,
    addCalendar,
    updateCalendar,
    deleteCalendar,
    setCalendars,
    eventFilters,
    setEventFilters
  } = useCalendarStore();

  const { 
    createCalendar, 
    updateCalendar: updateCalendarApi, 
    deleteCalendar: deleteCalendarApi, 
    fetchCalendars,
    fetchEvents
  } = useCalendarApi();

  const [isAddingCalendar, setIsAddingCalendar] = useState(false);
  const [newCalendarName, setNewCalendarName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);
  
  // Unified create dropdown state
  const [isCreateDropdownOpen, setIsCreateDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsCreateDropdownOpen(false);
      }
    };

    if (isCreateDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isCreateDropdownOpen]);
  
  // Calendar management modal state
  const [managingCalendar, setManagingCalendar] = useState<any>(null);
  const [editingName, setEditingName] = useState('');
  const [editingColor, setEditingColor] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  // Load filter expanded state from localStorage or default to true
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(() => {
    const saved = localStorage.getItem('calendarFiltersExpanded');
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Save filter expanded state to localStorage
  const toggleFiltersExpanded = () => {
    const newState = !isFiltersExpanded;
    setIsFiltersExpanded(newState);
    localStorage.setItem('calendarFiltersExpanded', JSON.stringify(newState));
  };

  // Filter functions
  const toggleEventType = (eventType: string) => {
    const currentTypes = eventFilters.eventTypes || [];
    const newTypes = currentTypes.includes(eventType)
      ? currentTypes.filter(type => type !== eventType)
      : [...currentTypes, eventType];
    
    setEventFilters({
      ...eventFilters,
      eventTypes: newTypes
    });
  };

  const togglePriority = (priority: string) => {
    const currentPriorities = eventFilters.priorities || [];
    const newPriorities = currentPriorities.includes(priority)
      ? currentPriorities.filter(p => p !== priority)
      : [...currentPriorities, priority];
    
    setEventFilters({
      ...eventFilters,
      priorities: newPriorities
    });
  };

  const getPriorityColor = (color: string) => {
    switch (color) {
      case 'red': return '#ef4444';
      case 'orange': return '#f59e0b';
      case 'yellow': return '#eab308';
      case 'green': return '#10b981';
      default: return '#6b7280';
    }
  };

  const handleRefreshCalendars = async () => {
    try {
      setIsRefreshing(true);
      console.log('🔄 [CalendarSidebar] Manually refreshing calendars...');
      
      const calendarsData = await fetchCalendars();
      console.log('✅ [CalendarSidebar] Calendars refreshed:', calendarsData?.length || 0);
      
      if (calendarsData && Array.isArray(calendarsData)) {
        setCalendars(calendarsData);
      }
    } catch (error) {
      console.error('❌ [CalendarSidebar] Failed to refresh calendars:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleAddCalendar = async () => {
    if (!newCalendarName.trim()) return;
    
    try {
      setIsCreating(true);
      setCreateError(null);
      setCreateSuccess(null);
      
      console.log('🔄 [CalendarSidebar] Creating calendar:', newCalendarName.trim());
      
      // Use the real API to create the calendar
      const backendCalendar = await createCalendar({
        name: newCalendarName.trim(),
        color: '#3B82F6',
        description: `Personal calendar: ${newCalendarName.trim()}`,
        is_default: false
      });
      
      console.log('✅ [CalendarSidebar] Calendar created successfully:', backendCalendar);
      
      // Convert backend response (snake_case) to frontend format (camelCase)
      addCalendar({
        id: backendCalendar.id || `temp-${Date.now()}`,
        name: backendCalendar.name || 'Untitled Calendar',
        color: backendCalendar.color || '#3B82F6',
        ownerId: (backendCalendar as any).owner_id || 'unknown',
        isDefault: (backendCalendar as any).is_default || false,
        permissions: [],
        isVisible: true,
        createdAt: new Date((backendCalendar as any).created_at || Date.now()),
        updatedAt: new Date((backendCalendar as any).updated_at || Date.now())
      });
      
      // Show success feedback
      setCreateSuccess(`"${newCalendarName.trim()}" calendar created successfully!`);
      
      // Reset form
      setNewCalendarName('');
      setIsAddingCalendar(false);
      
      // Refresh calendars to ensure we have the latest data
      await handleRefreshCalendars();
      
      // Clear success message after 3 seconds
      setTimeout(() => setCreateSuccess(null), 3000);
      
    } catch (error) {
      console.error('❌ [CalendarSidebar] Failed to create calendar:', error);
      
      let errorMessage = 'Failed to create calendar';
      if (error instanceof Error) {
        if (error.message.includes('duplicate key') || error.message.includes('already exists')) {
          errorMessage = `A calendar named "${newCalendarName.trim()}" already exists. Please choose a different name.`;
        } else {
          errorMessage = error.message;
        }
      }
      
      setCreateError(errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancelAdd = () => {
    setNewCalendarName('');
    setIsAddingCalendar(false);
    setCreateError(null);
    setCreateSuccess(null);
  };

  const handleManageCalendar = (calendar: any) => {
    setManagingCalendar(calendar);
    setEditingName(calendar.name);
    setEditingColor(calendar.color);
  };

  const handleCloseManagement = () => {
    setManagingCalendar(null);
    setEditingName('');
    setEditingColor('');
  };

  const handleUpdateCalendar = async () => {
    if (!managingCalendar || !editingName.trim()) return;
    
    try {
      setIsUpdating(true);
      
      const updates = {
        name: editingName.trim(),
        color: editingColor
      };
      
      await updateCalendarApi(managingCalendar.id, updates);
      
      // Update local state
      updateCalendar(managingCalendar.id, {
        name: editingName.trim(),
        color: editingColor
      });
      
      handleCloseManagement();
      
    } catch (error) {
      console.error('❌ Failed to update calendar:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteCalendar = async () => {
    if (!managingCalendar) return;
    
    try {
      setIsDeleting(true);
      
      await deleteCalendarApi(managingCalendar.id);
      
      // Update local state
      deleteCalendar(managingCalendar.id);
      
      handleCloseManagement();
      
    } catch (error) {
      console.error('❌ Failed to delete calendar:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const getCalendarEventCount = (calendarId: string) => {
    // Count events in this calendar from the events store
    return (events || []).filter(event => event.calendarId === calendarId).length;
  };

  const colorOptions = [
    '#3B82F6', // blue
    '#10B981', // emerald
    '#EF4444', // red
    '#F59E0B', // amber
    '#8B5CF6', // violet
    '#EC4899', // pink
    '#06B6D4', // cyan
    '#84CC16', // lime
  ];

  if (!isCalendarSidebarOpen) {
    return null;
  }

  return (
    <div className="w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 h-full flex flex-col shadow-sm">
      {/* EdgeLex Header */}
      <div className="px-3 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCalendarSidebarOpen(false)}
              className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              title="Toggle sidebar"
            >
              <Bars3Icon className="h-4 w-4" />
            </button>
            <CalendarIcon className="h-5 w-5 text-lex-accent" />
            <span className="text-sm font-semibold text-gray-900 dark:text-white">EdgeLex</span>
            <span className="px-1.5 py-0.5 bg-lex-accent-soft text-lex-accent text-xs font-medium rounded">Legal</span>
          </div>
          <button
            onClick={handleRefreshCalendars}
            disabled={isRefreshing}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-50"
            title="Refresh calendars"
          >
            <ArrowPathIcon className={`h-4 w-4 text-gray-500 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
        
        {/* Unified Create Button with Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsCreateDropdownOpen(!isCreateDropdownOpen)}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-lex-accent hover:bg-lex-accent-hover text-lex-accent-contrast rounded-md text-sm font-medium shadow-sm transition-colors"
          >
            <div className="flex items-center gap-2">
              <PlusIcon className="h-4 w-4" />
              Create
            </div>
            <ChevronDownIcon className={`h-4 w-4 transition-transform ${isCreateDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
          
          {/* Create Dropdown Menu */}
          {isCreateDropdownOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50">
              <div className="py-1">
                <button
                  onClick={() => {
                    onCreateEvent?.();
                    setIsCreateDropdownOpen(false);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <CalendarDaysIcon className="h-4 w-4" />
                  Create Event
                </button>
                <button
                  onClick={() => {
                    setIsAddingCalendar(true);
                    setIsCreateDropdownOpen(false);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <CalendarIcon className="h-4 w-4" />
                  Create Calendar
                </button>
                <button
                  onClick={() => {
                    // TODO: Implement task creation when tasks feature is ready
                    console.log('Task creation clicked - feature not yet implemented');
                    setIsCreateDropdownOpen(false);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <ClipboardDocumentListIcon className="h-4 w-4" />
                  Create Task <span className="text-xs">(Coming Soon)</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* My Calendars Section Header */}
      <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white">My Calendars</h2>
      </div>

      {/* Success Feedback */}
      {createSuccess && (
        <div className="mx-3 mt-2 p-2 bg-green-100 border border-green-300 text-green-700 text-xs rounded">
          {createSuccess}
        </div>
      )}

      {/* Debug Info */}
      {(calendars || []).length === 0 && (
        <div className="mx-3 mt-2 p-2 bg-yellow-100 border border-yellow-300 text-yellow-700 text-xs rounded">
          No calendars loaded. Try clicking the refresh button ↑
        </div>
      )}

      {/* Calendar Creation Form - Only shown when adding */}
      {isAddingCalendar && (
        <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-lex-accent-soft">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Create New Calendar</h3>
          <div className="space-y-2">
            <input
              type="text"
              value={newCalendarName}
              onChange={(e) => setNewCalendarName(e.target.value)}
              placeholder="Calendar name..."
              className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-lex-accent"
              autoFocus
              disabled={isCreating}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isCreating) handleAddCalendar();
                if (e.key === 'Escape') handleCancelAdd();
              }}
            />
            
            {createError && (
              <div className="text-xs text-red-600 dark:text-red-400">
                {createError}
              </div>
            )}
            
            <div className="flex gap-1">
              <button
                onClick={handleAddCalendar}
                disabled={!newCalendarName.trim() || isCreating}
                className="flex-1 flex items-center justify-center gap-1 px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreating ? (
                  <>
                    <div className="animate-spin h-3 w-3 border border-white border-t-transparent rounded-full" />
                    Creating...
                  </>
                ) : (
                  <>
                    <CheckIcon className="h-3 w-3" />
                    Add
                  </>
                )}
              </button>
              <button
                onClick={handleCancelAdd}
                disabled={isCreating}
                className="flex-1 flex items-center justify-center gap-1 px-2 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600 disabled:opacity-50"
              >
                <XMarkIcon className="h-3 w-3" />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Compact Calendar List */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-2 space-y-1">
          {(calendars || []).map((calendar) => {
            const isVisible = (visibleCalendarIds || []).includes(calendar.id);
            const eventCount = getCalendarEventCount(calendar.id);
            
            return (
              <div
                key={calendar.id}
                className="group flex items-center gap-2 p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {/* Visibility Toggle with Color */}
                <button
                  onClick={() => {
                    console.log('👁️ [CalendarSidebar] Toggling visibility for calendar:', { 
                      id: calendar.id, 
                      name: calendar.name, 
                      currentlyVisible: isVisible 
                    });
                    toggleCalendarVisibility(calendar.id);
                  }}
                  className="flex-shrink-0 relative"
                >
                  <div
                    className="w-4 h-4 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: calendar.color }}
                  >
                    {isVisible ? (
                      <EyeIcon className="h-2.5 w-2.5 text-white" />
                    ) : (
                      <EyeSlashIcon className="h-2.5 w-2.5 text-white opacity-60" />
                    )}
                  </div>
                </button>

                {/* Calendar Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1">
                    <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {calendar.name}
                    </span>
                    {calendar.isDefault && (
                      <span className="px-1 py-0.5 text-xs bg-lex-accent-soft text-lex-accent rounded">
                        Default
                      </span>
                    )}
                    {(calendar.permissions || []).length > 0 && (
                      <ShareIcon className="h-3 w-3 text-gray-400 flex-shrink-0" />
                    )}
                  </div>
                  {eventCount > 0 && (
                    <span className="text-xs text-gray-500">
                      {eventCount} events
                    </span>
                  )}
                </div>

                {/* Settings - Only show on hover */}
                <button
                  onClick={() => handleManageCalendar(calendar)}
                  className="flex-shrink-0 p-1 opacity-0 group-hover:opacity-100 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-all"
                  title="Calendar settings"
                >
                  <Cog6ToothIcon className="h-3 w-3 text-gray-500" />
                </button>
              </div>
            );
          })}
          
          {(calendars || []).length === 0 && (
            <div className="text-center py-6 text-gray-500">
              <CalendarDaysIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p className="text-xs">No calendars yet</p>
              <p className="text-xs text-gray-400">Create your first calendar</p>
            </div>
          )}
        </div>

        {/* Collapsible Filters Section */}
        <div className="border-t border-gray-200 dark:border-gray-700 mt-2">
          {/* Filter Header - Always Visible */}
          <div className="p-3 pb-0">
            <button
              onClick={toggleFiltersExpanded}
              className="w-full flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-700 p-2 -m-2 rounded transition-colors"
            >
              <div className="flex items-center gap-2">
                <FunnelIcon className="h-4 w-4 text-gray-500" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Filters</h3>
                {(eventFilters.eventTypes.length > 0 || eventFilters.priorities.length > 0) && (
                  <span className="px-1.5 py-0.5 bg-lex-accent-soft text-lex-accent text-xs rounded-full">
                    {eventFilters.eventTypes.length + eventFilters.priorities.length}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {(eventFilters.eventTypes.length > 0 || eventFilters.priorities.length > 0) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEventFilters({ eventTypes: [], priorities: [], attendees: [], dateRange: null });
                    }}
                    className="text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 px-2 py-0.5 rounded transition-colors"
                  >
                    Clear
                  </button>
                )}
                {isFiltersExpanded ? (
                  <ChevronUpIcon className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                )}
              </div>
            </button>
          </div>

          {/* Collapsible Filter Content */}
          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isFiltersExpanded ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
          }`}>
            <div className="p-3 pt-2 space-y-3">

            {/* Event Types */}
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                Event Types
              </h4>
              <div className="space-y-1">
                {EVENT_TYPES.map((type) => {
                  const isActive = eventFilters.eventTypes.includes(type.id);
                  const Icon = type.icon;
                  
                  return (
                    <button
                      key={type.id}
                      onClick={() => toggleEventType(type.id)}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded transition-colors ${
                        isActive
                          ? 'bg-lex-accent-soft text-lex-accent border border-lex-accent'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{type.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Priority Levels */}
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                Priority
              </h4>
              <div className="space-y-1">
                {PRIORITY_LEVELS.map((priority) => {
                  const isActive = eventFilters.priorities.includes(priority.id);
                  
                  return (
                    <button
                      key={priority.id}
                      onClick={() => togglePriority(priority.id)}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded transition-colors ${
                        isActive
                          ? 'bg-lex-accent-soft text-lex-accent border border-lex-accent'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    >
                      <div 
                        className={`w-2 h-2 rounded-full flex-shrink-0`}
                        style={{ backgroundColor: getPriorityColor(priority.color) }}
                      />
                      <span className="truncate capitalize">{priority.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Active Filter Summary */}
            {(eventFilters.eventTypes.length > 0 || eventFilters.priorities.length > 0) && (
              <div className="pt-2 border-t border-gray-100">
                <div className="text-xs text-gray-600 space-y-1">
                  {eventFilters.eventTypes.length > 0 && (
                    <div>
                      <span className="font-medium">Types:</span> {eventFilters.eventTypes.length} selected
                    </div>
                  )}
                  {eventFilters.priorities.length > 0 && (
                    <div>
                      <span className="font-medium">Priority:</span> {eventFilters.priorities.length} selected
                    </div>
                  )}
                </div>
              </div>
                         )}
             </div>
           </div>
         </div>
       </div>

      {/* Compact Footer */}
      <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700">
        <div className="text-xs text-gray-500 text-center">
          {(calendars || []).length} calendar{(calendars || []).length !== 1 ? 's' : ''} • {(visibleCalendarIds || []).length} visible
        </div>
        
        {/* Debug Info - can be removed later */}
        <details className="mt-2">
          <summary className="text-xs text-gray-400 cursor-pointer">Debug Info</summary>
          <div className="mt-1 p-2 bg-gray-100 dark:bg-gray-700 rounded text-xs">
            <div><strong>Calendars:</strong> {(calendars || []).length}</div>
            <div><strong>Visible IDs:</strong> {JSON.stringify(visibleCalendarIds || [])}</div>
            <div><strong>Calendar Details:</strong></div>
            {(calendars || []).map(cal => (
              <div key={cal.id} className="ml-2">
                • {cal.name} ({cal.id?.slice(-8) || 'no-id'}) - {(visibleCalendarIds || []).includes(cal.id) ? '👁️ Visible' : '🚫 Hidden'}
              </div>
            ))}
            <div className="mt-2"><strong>Events in Store:</strong> {(events || []).length}</div>
            {(events || []).slice(0, 3).map((event: CalendarEvent) => (
              <div key={event.id} className="ml-2 text-xs">
                • {event.title || 'No title'} ({event.id?.slice(-8) || 'no-id'}) - Cal: {event.calendarId?.slice(-8) || 'no-cal'} - {event.start?.toLocaleDateString() || 'no-date'}
              </div>
            ))}
            {(events || []).length > 3 && <div className="ml-2">... and {(events || []).length - 3} more</div>}
            <div className="mt-2">
              <button 
                onClick={async () => {
                  console.log('🔄 [Debug] Manual event refresh triggered');
                  try {
                    const refreshedEvents = await fetchEvents();
                    console.log('✅ [Debug] Manual refresh result:', refreshedEvents?.length || 0, 'events');
                  } catch (error) {
                    console.error('❌ [Debug] Manual refresh failed:', error);
                  }
                }}
                className="px-2 py-1 bg-lex-accent text-lex-accent-contrast rounded text-xs hover:bg-lex-accent-hover"
              >
                Refresh Events
              </button>
            </div>
          </div>
        </details>
      </div>

      {/* Calendar Management Modal */}
      {managingCalendar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-96 max-w-sm mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Manage Calendar
            </h3>
            
            <div className="space-y-4">
              {/* Calendar Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Calendar Name
                </label>
                <input
                  type="text"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-lex-accent"
                />
              </div>

              {/* Color Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Calendar Color
                </label>
                <div className="flex flex-wrap gap-2">
                  {colorOptions.map((color) => (
                    <button
                      key={color}
                      onClick={() => setEditingColor(color)}
                      className={`w-8 h-8 rounded-full border-2 ${
                        editingColor === color ? 'border-gray-900 dark:border-white' : 'border-gray-300'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4">
                <button
                  onClick={handleUpdateCalendar}
                  disabled={!editingName.trim() || isUpdating}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-md hover:bg-lex-accent-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUpdating ? (
                    <>
                      <div className="animate-spin h-4 w-4 border border-white border-t-transparent rounded-full" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <PencilIcon className="h-4 w-4" />
                      Update
                    </>
                  )}
                </button>
                
                {!managingCalendar.isDefault && (
                  <button
                    onClick={handleDeleteCalendar}
                    disabled={isDeleting}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isDeleting ? (
                      <>
                        <div className="animate-spin h-4 w-4 border border-white border-t-transparent rounded-full" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <TrashIcon className="h-4 w-4" />
                        Delete
                      </>
                    )}
                  </button>
                )}
              </div>

              <button
                onClick={handleCloseManagement}
                disabled={isUpdating || isDeleting}
                className="w-full px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarSidebar;
